import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { adminTelegramAPI } from '@/api';
import { Modal, Button, Label } from '@/components/ui';
import { BranchFilterSelect } from '@/components/common';
import { Send } from 'lucide-react';

type Target = 'all' | 'selected' | 'branch' | 'department' | 'role';

const ROLES = ['super_admin', 'hr_admin', 'accountant', 'branch_manager', 'rop_manager', 'viewer'];

const MAX_LEN = 4000;

const BroadcastModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  selectedIds: number[];
  branches: any[];
  departments: any[];
  onSent?: () => void;
}> = ({ isOpen, onClose, selectedIds, branches, departments, onSent }) => {
  const [target, setTarget] = useState<Target>('all');
  const [message, setMessage] = useState('');
  const [branchId, setBranchId] = useState<number | ''>('');
  const [departmentId, setDepartmentId] = useState<number | ''>('');
  const [role, setRole] = useState('');
  const [confirmShown, setConfirmShown] = useState(false);

  const charsLeft = MAX_LEN - message.length;

  const targetSummary = useMemo(() => {
    switch (target) {
      case 'all':
        return 'Всем подтверждённым подключениям';
      case 'selected':
        return `Выбранным подключениям: ${selectedIds.length}`;
      case 'branch':
        return branchId ? `Филиал: ${branches.find((b: any) => b.id === branchId)?.name ?? branchId}` : 'Филиал не выбран';
      case 'department':
        return departmentId
          ? `Отдел: ${departments.find((d: any) => d.id === departmentId)?.name ?? departmentId}`
          : 'Отдел не выбран';
      case 'role':
        return role ? `Роль: ${role}` : 'Роль не выбрана';
    }
  }, [target, selectedIds, branchId, departmentId, role, branches, departments]);

  const broadcastMut = useMutation({
    mutationFn: () =>
      adminTelegramAPI.broadcast({
        message,
        target,
        link_ids: target === 'selected' ? selectedIds : undefined,
        branch_id: target === 'branch' ? Number(branchId) : undefined,
        department_id: target === 'department' ? Number(departmentId) : undefined,
        role: target === 'role' ? role : undefined,
      }),
    onSuccess: (res) => {
      const sent = res?.data?.data?.queued ?? res?.data?.queued ?? 0;
      toast.success(`Рассылка поставлена в очередь: ${sent} сообщений`);
      setMessage('');
      setConfirmShown(false);
      onSent?.();
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? 'Не удалось отправить рассылку';
      toast.error(msg);
    },
  });

  const isValid = useMemo(() => {
    if (!message.trim()) return false;
    if (message.length > MAX_LEN) return false;
    if (target === 'selected' && selectedIds.length === 0) return false;
    if (target === 'branch' && !branchId) return false;
    if (target === 'department' && !departmentId) return false;
    if (target === 'role' && !role) return false;
    return true;
  }, [message, target, selectedIds, branchId, departmentId, role]);

  const handleSubmit = () => {
    if (!isValid) return;
    if (!confirmShown) {
      setConfirmShown(true);
      return;
    }
    broadcastMut.mutate();
  };

  const handleClose = () => {
    if (broadcastMut.isPending) return;
    setConfirmShown(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Telegram-рассылка" maxWidthClass="max-w-2xl">
      <div className="space-y-4">
        <div>
          <Label>Кому отправить</Label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-1">
            {(['all', 'selected', 'branch', 'department', 'role'] as Target[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTarget(t)}
                className={`px-3 py-2 text-sm rounded border transition ${
                  target === t
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
              >
                {t === 'all' && 'Всем'}
                {t === 'selected' && `Выбранным (${selectedIds.length})`}
                {t === 'branch' && 'Филиал'}
                {t === 'department' && 'Отдел'}
                {t === 'role' && 'Роль'}
              </button>
            ))}
          </div>
        </div>

        {target === 'branch' && (
          <div>
            <Label>Филиал</Label>
            <BranchFilterSelect
              alwaysShow
              className="w-full border rounded px-2 py-2 text-sm mt-1"
              value={String(branchId)}
              onChange={(value) => setBranchId(value ? Number(value) : '')}
              allLabel="— Выберите —"
            />
          </div>
        )}

        {target === 'department' && (
          <div>
            <Label>Отдел</Label>
            <select
              className="w-full border rounded px-2 py-2 text-sm mt-1"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">— Выберите —</option>
              {departments.map((d: any) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        )}

        {target === 'role' && (
          <div>
            <Label>Роль</Label>
            <select
              className="w-full border rounded px-2 py-2 text-sm mt-1"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="">— Выберите —</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
        )}

        <div>
          <Label>Текст сообщения</Label>
          <textarea
            className="w-full mt-1 border rounded px-3 py-2 text-sm font-mono min-h-[160px]"
            placeholder="Поддерживается HTML-форматирование: <b>...</b>, <i>...</i>, <a href='...'>...</a>"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={MAX_LEN + 100}
          />
          <div className={`text-xs mt-1 text-right ${charsLeft < 0 ? 'text-rose-600' : 'text-slate-500'}`}>
            Осталось символов: {charsLeft}
          </div>
        </div>

        <div className="rounded border bg-slate-50 px-3 py-2 text-sm">
          <div className="text-xs uppercase text-slate-500 mb-1">Получатели</div>
          <div className="font-medium">{targetSummary}</div>
        </div>

        {confirmShown && (
          <div className="rounded border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            После отправки рассылку нельзя отменить. Подтвердите продолжение.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={handleClose} disabled={broadcastMut.isPending}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || broadcastMut.isPending}
          >
            <Send className="h-4 w-4 mr-1" />
            {confirmShown ? (broadcastMut.isPending ? 'Отправка...' : 'Подтвердить и отправить') : 'Отправить'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BroadcastModal;
